import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */

/** API */
import LabsApi from 'api/setting/default-labs/lab.api'

/** UTILS || SLICE */
import { resetModal, submitModal, unSubmitModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import HeaderCard from 'component/CardCustom/HeaderCard'
import TableRowExport from 'component/Pdf/TableRowExport'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import RadioButton from 'new-components/buttons/radio-button'
import SwitchButton from 'new-components/buttons/switch-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import PageModal from 'new-components/modal/page-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import { dateTimeToView } from 'utils/date.utils'
import { exportLabsToExcel } from './exportLabsToExcel'

const permissions: any = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  LAB_NAME: ``,
  IS_DUPLICATE_LAB_NAME: ``,
  LAB_NAME_STRING_BASE: ``,
  LAB_NAME_STRING_EMPTY: ``,
  LAB_NAME_MAXIMUM_LENGTH: ``,
  LAB_NAME_ANY_REQUIRED: ``
}
export default function Labs() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const componentRef = useRef(null)
  const [labs, setLabs] = useState([])
  const [exportData, setExportData] = useState([])

  const [labId, setLabId] = useState(Number)
  const [labName, setLabName] = useState('')
  const [inputSupport, setInputSupport] = useState('NOT_SUPPORT')
  const [labStatus, setLabStatus] = useState('ACTIVE')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [disabledSubmit, setDisabledSubmit] = useState(false)

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)


  const loadData = async () => {
    try {
      let condition = {}
      condition = {
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      }

      const resp = await LabsApi.findAll(condition)
      const respExport = await LabsApi.findAll({ ...condition, export: 1 })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setLabs(resp.data)
        setExportData(respExport.data)
      } else {
        setLabs([])
        setExportData([])
      }
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const submit = () => {
    if (!labName) return setErrorMessage({ ...errorMessage, ...{ LAB_NAME: t('LAB.MESSAGE.LAB_NAME') } })

    setDisabledSubmit(true)
    dispatch(submitModal())
    LabsApi.create({ labName, inputSupport, status: labStatus })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('LAB.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`LAB.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
        setDisabledSubmit(false)
      })
      .catch((e) => {
        const err = e.response.data
        setDisabledSubmit(false)
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`LAB.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const submitEdit = () => {
    if (!labName) return setErrorMessage({ ...errorMessage, ...{ LAB_NAME: t(`LAB.MESSAGE.LAB_NAME`) } })

    setDisabledSubmit(true)
    dispatch(submitModal())
    LabsApi.update(labId, { labName, inputSupport, status: labStatus })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`LAB.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`LAB.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
        setDisabledSubmit(false)
      })
      .catch((e) => {
        const err = e.response.data
        setDisabledSubmit(false)
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`LAB.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setLabId(0)
    setLabName('')
    setLabStatus('ACTIVE')
    clearErrorMessage()
    setIsPopup(false)
  }

  const onActive = () => {
    if (id) {
      LabsApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('LAB.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('LAB.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onDelete = () => {
    if (id) {
      LabsApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('LAB.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('LAB.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { labId: lbId, labName: lbName, status: lbStatus, inputSupport: lbInputSupport } = data
    setLabId(lbId)
    setLabName(lbName)
    setLabStatus(lbStatus)
    setInputSupport(lbInputSupport)
    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'labName', disablePadding: false, label: t('LAB.TABLE.CELL.LAB_NAME') },
    { id: 'status', disablePadding: false, label: t('LAB.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('LAB.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number, valueExportData: boolean) => {
    const num = valueExportData === true ? no + 1 : page * pageLimit - pageLimit + no + 1
    const { labId: lbId, labName: lbName, updatedBy, updatedAt, status: lbStatus } = objData
    const statusBtnActive = lbStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = lbStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: lbId,
      id: lbId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: num },
        { option: 'TEXT', align: 'left', label: lbName },
        { option: 'STATUS', align: 'center', label: lbStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <>
        {valueExportData === true && <TableRowExport {...objRenderData} /> ||
          <TableRowCommon
            {...objRenderData}
            onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
            oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
            onedit={() => onEdit(objRenderData.obj)}
            ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
          />
        }
      </>
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.labName)
  }

  const filter = [
    { label: `${t('INPUT.SEARCH')}: ${search || '-'}` },
    { label: `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}` }
  ]

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`LAB.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`LAB.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { labName: name, status: t(`LAB.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <PageModal
        title={labId ? t('LAB.TITLE_UPDATE') : t('LAB.TITLE_CREATE')}
        isOpen={isPopup}
        onCancel={{ onClick: () => resetForm() }}
        onSubmit={{ onClick: () => labId ? submitEdit() : submit(), disabled: disabledSubmit }}
        handleClose={resetForm}
        size={'XS'}
      >
        <Box paddingY="8px">
          {labId ? (
            <Box display="flex" justifyContent="space-between" alignItems="center" paddingBottom="8px">
              <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
              <SwitchButton
                onChange={() => setLabStatus(labStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                checked={labStatus === 'ACTIVE' ? true : false}
              />
            </Box>
          ) : (
            ''
          )}

          <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
            <TextInput
              fullWidth
              onChange={(event) => {
                setLabName(event.target.value)
                clearErrorMessage()
              }}
              value={labName}
              label={t('LAB.INPUT.LAB_NAME')}
              helperText={errorMessage.LAB_NAME || errorMessage.IS_DUPLICATE_LAB_NAME || errorMessage.LAB_NAME_STRING_BASE || errorMessage.LAB_NAME_STRING_EMPTY || errorMessage.LAB_NAME_MAXIMUM_LENGTH || errorMessage.LAB_NAME_ANY_REQUIRED}
              required={true}
            />
            <Box>
              <Box>
                <Typography >{t('LAB.TITLE_SUPPORT')}</Typography>
              </Box>
              <Box padding="0 12px">
                <RadioButton
                  name="radio-buttons-group"
                  value={inputSupport}
                  sx={{ gap: '24px', flexDirection: 'row' }}
                  onChange={(e) => {
                    setInputSupport(e.target.value)
                  }}
                  options={[
                    { label: t('LAB.INPUT.RADIO.SUPPORT'), value: 'SUPPORT', },
                    { label: t('LAB.INPUT.RADIO.NOT_SUPPORT'), value: 'NOT_SUPPORT', }
                  ]}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </PageModal>
      <HeaderCard text={t('LAB.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('LAB.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={8}>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <MainButton
                title={t('INPUT.SEARCH')}
                onClick={() => {
                  setPage(1)
                  loadData()
                }}
                type={'primary'}
                variant={'contained'}
                style={{ width: '120px' }}
              />
              <Box display="flex" flexDirection="row" alignItems="center" gap='16px'>
                <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                  if (e === 'Excel') {
                    const report: any = {
                      title: t('LAB.TITLE'),
                      statusFilter: filter[1].label,
                      rows: exportData.map((item: any) => ({
                        labName: item?.labName || '-',
                        status: t(`STATUS.${item?.status}`),
                        updatedBy: item?.updatedBy || '-',
                        updatedAt: dateTimeToView(item?.updatedAt) || '-'
                      })),
                      t
                    }
                    exportLabsToExcel(report)
                  } else if (e === 'PDF') {
                    const style = document.createElement("style");
                    style.innerHTML = `
                      @media print {
                        @page { size: portrait }
                      }
                    `;
                    document.head.appendChild(style);
                    window.print();
                    document.head.removeChild(style);

                  }
                }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                <MainButton title={t('LAB.BUTTON.ADD')} disabled={permissions.isCreate.disabled} type='primary' variant='contained' onClick={() => setIsPopup(true)} startIcon={<AddCircleIcon />} />
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={labs.map((val, i) => renderData(val, i, false))}
          />

          <Box
            id="print-table-container"
            ref={componentRef}
          >
            <Typography fontSize="14px" fontWeight={400}>{t('LAB.TITLE')}</Typography>
            <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
              <Typography fontSize="10px" fontWeight={400}>{t('STATUS.LABEL')}: {status === 'ALL' ? t('STATUS.ALL') : status === 'ACTIVE' ? t('STATUS.ACTIVE') : t('STATUS.INACTIVE')}</Typography>
            </Box>
            <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
              <thead>
                <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
                  <th style={{ borderBottom: '1px solid black' }}>{t('#')}</th>
                  <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('LAB.TABLE.CELL.LAB_NAME')}</th>
                  <th style={{ borderBottom: '1px solid black' }}>{t('LAB.TABLE.CELL.STATUS')}</th>
                  <th style={{ borderBottom: '1px solid black' }}>{t('LAB.TABLE.CELL.UPDATED_BY')}</th>
                </tr>
              </thead>
              <tbody>
                {exportData?.length === 0 &&
                  <tr>
                    <td colSpan={4} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
                  </tr>
                }
                {exportData.map((data: any, index: number) => (
                  <tr key={index}>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{index + 1}</td>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{data?.labName || '-'}</td>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100px' }}>{t(`STATUS.${data?.status}`) || '-'}</td>
                    <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                      <span>{data?.updatedBy || '-'}</span>
                      <span>{dateTimeToView(data?.updatedAt) || '-'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      </Box>
    </Box >
  )
}
